import RefreshIcon from "@assets/images/refresh-primary.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from "@material-ui/core";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import SearchIcon from "@material-ui/icons/Search";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import DatePicker from "@saleor/components/DatePicker";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import useAppState from "@saleor/hooks/useAppState";
import useOrderFilterInquiry from "@saleor/hooks/useOrderFilterInquiry";
import { mapEdgesToItems } from "@saleor/utils/maps";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import { OrderListFilterProps } from "../OrderListFilter";
import { DateSelectionType, FIELD_TYPE } from "./constant";
import useStyles from "./styles";

const OrderFilterSection: React.FC<OrderListFilterProps> = ({
  handleChangeFilter,
  message,
  onClearForm,
  isOpen,
  onToggleFilter,
  loading
}) => {
  const classes = useStyles();
  const [isFirstRender, setIsFirstRender] = useState<boolean>(false);
  const [{ filterOrder: filterAppState }] = useAppState();
  const {
    formik,
    setDataFormik,
    isClean,
    handleCleanForm,
    formData,
    fetchMaterialCodeDataSuggestion,
    loadingMaterialCodeDataSuggestion,
    getMaterialCodeDataSuggestion
  } = useOrderFilterInquiry({ handleChangeFilter });

  if (filterAppState && Object.keys(filterAppState)?.length && !isFirstRender) {
    setDataFormik({
      ...filterAppState,
      gte: filterAppState.createDate
        ? filterAppState.createDate.gte
        : filterAppState.lastUpdate
        ? filterAppState.lastUpdate.gte
        : "",
      lte: filterAppState.createDate
        ? filterAppState.createDate.lte
        : filterAppState.lastUpdate
        ? filterAppState.lastUpdate.lte
        : "",
      dateSelectionType: filterAppState.createDate
        ? DateSelectionType.CREATE_DATE
        : DateSelectionType.LAST_UPDATE,
      statusSAP: filterAppState.statusSap?.length
        ? filterAppState.statusSap?.length === 1
          ? filterAppState.statusSap[0]
          : "ALL"
        : ""
    });
    setIsFirstRender(true);
  }

  const getTextError = field => (
    <div className={classes.textError}>{formik.errors[field]}</div>
  );

  const isDisabled = useMemo(() => {
    if (
      formik.values.soNo ||
      formik.values.invoiceNo ||
      formik.values.contractNo ||
      formik.values.dpNo ||
      formik.values.purchaseOrderNo
    ) {
      return false;
    } else {
      return !(formik.values.gte && formik.values.lte);
    }
  }, [formik.values, formik.dirty]);

  const mappingDataSuggest = name => {
    if (name === "materialCodeName") {
      return {
        fetchDataSuggestions: fetchMaterialCodeDataSuggestion,
        loadingDataSuggestion: loadingMaterialCodeDataSuggestion,
        getDataSuggestions: getMaterialCodeDataSuggestion,
        searchField: "material",
        isCheckingRole: true,
        dataOptions:
          mapEdgesToItems(
            fetchMaterialCodeDataSuggestion?.filterRequireAttentionMaterial
          )?.map(data => ({
            title: `${data.code} - ${data.descriptionEn}`,
            value: `${data.code}`
          })) || [],
        totalCount:
          fetchMaterialCodeDataSuggestion?.filterRequireAttentionMaterial?.edges
            ?.length
      };
    }
  };

  useEffect(() => {
    if (formik.values.lte) {
      const maxDiff = 90;
      const diff = moment(formik.values.lte).diff(
        moment(formik.values.gte),
        "day"
      );
      if (Math.abs(diff) > maxDiff) {
        const newLte = moment(formik.values.gte).add(maxDiff, "day");
        formik.setFieldValue("lte", newLte);
      }
    }
  }, [formik.values.gte]);

  useEffect(() => {
    if (formik.values.gte) {
      const maxDiff = 90;
      const diff = moment(formik.values.gte).diff(
        moment(formik.values.lte),
        "day"
      );
      if (Math.abs(diff) > maxDiff) {
        const newGte = moment(formik.values.lte).subtract(maxDiff, "day");
        formik.setFieldValue("gte", newGte);
      }
    }
  }, [formik.values.lte]);

  const renderItem = (
    <Grid className={classes.searchBox} container spacing={3}>
      {formData.map((child, key) => {
        const renderChild = (children: JSX.Element) => (
          <Grid item md={3} sm={6} key={key}>
            <MyTypography variant={"subtitle3"}>
              {child.title}
              {child.required && <span className={classes.required}>*</span>}
            </MyTypography>
            {children}
            {getTextError(child.name)}
          </Grid>
        );
        if (child.type === FIELD_TYPE.SINGLE_INPUT) {
          return renderChild(
            <SingleTextField
              name={child.name}
              id={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              disabled={child.disable}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              maxLengthProps={child?.maxLength ? child.maxLength : undefined}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              onChange={formik.handleChange}
              value={formik.values[child.name]}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
            />
          );
        }

        if (child.type === FIELD_TYPE.SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggest(child.name);

          return renderChild(
            <InputSuggestion
              {...dataSuggest}
              contained
              key={`input${key}`}
              name={child.name}
              id={child.name}
              defaultInputValue={formik.values[child.name]}
              defaultValue={{
                title: formik.values[child.name],
                value: formik.values[child.name]
              }}
              objectSuggestion
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              isClean={isClean}
              handleSelectedSearch={e => {
                formik.handleChange({
                  target: {
                    name: child.name,
                    value: typeof e === "string" ? e : e.value
                  }
                });
              }}
            />
          );
        }

        if (child.type === FIELD_TYPE.RADIO_GROUP) {
          return renderChild(
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name={child.name}
              id={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
            >
              {child.options.map((o, oKey) => (
                <FormControlLabel
                  key={`radio${oKey}`}
                  id={`radio${oKey}`}
                  value={o.value}
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>{o.label}</MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          );
        }
        if (child.type === FIELD_TYPE.DATEPICKER) {
          if (child.name === "gte") {
            return renderChild(
              <DatePicker
                name={child.name}
                id={child.name}
                disableManualInput
                value={formik.values[child.name] || ""}
                onChange={formik.handleChange}
                format="YYYY-MM-DD"
                error={!!formik.errors[child.name]}
                maxDate={
                  formik.values.lte ? moment(formik.values.lte) : undefined
                }
                minDate={undefined}
              />
            );
          } else if (child.name === "lte") {
            return renderChild(
              <DatePicker
                name={child.name}
                id={child.name}
                disableManualInput
                value={formik.values[child.name] || ""}
                onChange={formik.handleChange}
                format="YYYY-MM-DD"
                error={!!formik.errors[child.name]}
                maxDate={undefined}
                minDate={
                  formik.values.gte ? moment(formik.values.gte) : undefined
                }
              />
            );
          }
          return renderChild(
            <DatePicker
              name={child.name}
              id={child.name}
              disableManualInput
              value={formik.values[child.name] || ""}
              onChange={formik.handleChange}
              format="YYYY-MM-DD"
              maxDate={
                child.name === "gte"
                  ? formik.values.lte
                    ? moment(formik.values.lte)
                    : undefined
                  : formik.values.gte
                  ? moment(formik.values.gte).add(1, "y")
                  : undefined
              }
              minDate={
                child.name === "lte"
                  ? formik.values.gte
                    ? moment(formik.values.gte)
                    : undefined
                  : formik.values.lte
                  ? moment(formik.values.lte).subtract(1, "y")
                  : undefined
              }
              error={!!formik.errors[child.name]}
            />
          );
        }
      })}
    </Grid>
  );

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  return (
    <>
      <Grid className={classes.header} container onClick={onToggleFilter}>
        <MyTypography variant="h6">
          <FormattedMessage defaultMessage="ค้นหาคำสั่งซื้อ" />
        </MyTypography>
        <ArrowIconComponent className={classes.arrowIcon} />
      </Grid>
      <Collapse in={isOpen}>
        {renderItem}
        <Box className={classes.divider}>
          <FormSpacer />
          <Divider />
        </Box>
        <Box className={classes.bottomBar} ml={3}>
          {message}
          <Box className={classes.flex}>
            <Box mr={2}>
              <Button
                className={classes.resetButton}
                variant={"outlined"}
                color={"secondary"}
                disabled={!formik.dirty}
                onClick={() => {
                  onClearForm();
                  handleCleanForm();
                }}
                name="resetButton"
                id="resetButton"
              >
                <SVG
                  src={RefreshIcon}
                  className={classes.icon}
                  width={16}
                  height={16}
                />
                <FormattedMessage defaultMessage="ล้างข้อมูล" />
              </Button>
            </Box>
            <Box>
              <Button
                className={classes.actionButton}
                variant={"contained"}
                disabled={isDisabled || loading}
                color={"secondary"}
                onClick={formik.submitForm}
                name="submitButton"
                id="submitButton"
              >
                <SearchIcon style={{ fontSize: "18px" }} />
                <FormattedMessage defaultMessage="ค้นหา" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default OrderFilterSection;
